import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Nav, Table } from 'react-bootstrap';
import { isChangeManager } from '../changePermissions';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeCatalogItem,
  ChangeFormField,
  ChangeWorkGroup,
  InternalSubtaskTemplate,
} from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import ChangeFormFieldBuilder from './ChangeFormFieldBuilder';
import ChangeInternalSubtaskTabsEditor from './ChangeInternalSubtaskTabsEditor';
import ChangeInternalSubtaskPresetCatalogPanel from './ChangeInternalSubtaskPresetCatalogPanel';

const ChangeInternalSubtaskTemplatesPanel: React.FC = () => {
  const { user } = useAuth();
  const { showConfirm, showAlert } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManage = isChangeManager(user);
  const requestedId = Number(searchParams.get('id') || 0);
  const [items, setItems] = useState<InternalSubtaskTemplate[]>([]);
  const [groups, setGroups] = useState<ChangeWorkGroup[]>([]);
  const [taskTypes, setTaskTypes] = useState<ChangeCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [defaultTitle, setDefaultTitle] = useState('');
  const [taskTypeId, setTaskTypeId] = useState<number | ''>('');
  const [allowThread, setAllowThread] = useState(false);
  const [showOnDispatch, setShowOnDispatch] = useState(true);
  const [active, setActive] = useState(true);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [formTabs, setFormTabs] = useState<string[]>([]);
  const [formTabsPosition, setFormTabsPosition] = useState<'before' | 'after'>('after');
  const [savingMeta, setSavingMeta] = useState(false);
  const [savingTabs, setSavingTabs] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hubTab, setHubTab] = useState<'models' | 'presets'>('models');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tpl, types, workGroups] = await Promise.all([
        changesApi.listInternalSubtaskTemplates({ catalog_only: true }),
        changesApi.listCatalog('task-types', { only_active: true }).catch(() => ({ items: [] })),
        changesApi.listWorkGroups({ only_active: true }).catch(() => ({ items: [] })),
      ]);
      setItems(tpl.items || []);
      setTaskTypes(types.items || []);
      setGroups(workGroups.items || []);
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar as subtarefas internas.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!requestedId || loading) return;
    const row = items.find((item) => item.id === requestedId);
    if (row) applyRowToForm(row);
  }, [requestedId, items, loading]);

  const editing = useMemo(
    () => (editId != null ? items.find((item) => item.id === editId) || null : null),
    [editId, items],
  );

  const applyRowToForm = (row: InternalSubtaskTemplate) => {
    setEditId(row.id);
    setName(row.name);
    setDefaultTitle(row.default_title || '');
    setTaskTypeId(row.task_type_id ?? '');
    setAllowThread(Boolean(row.allow_thread_default));
    setShowOnDispatch(row.show_on_dispatch !== false);
    setActive(row.is_active !== false);
    setSelectedGroupIds(row.linked_group_ids || []);
    setFormTabs(row.form_tabs || []);
    setFormTabsPosition(row.form_tabs_position === 'before' ? 'before' : 'after');
  };

  const closeEditor = () => {
    setEditId(null);
    setName('');
    setDefaultTitle('');
    setTaskTypeId('');
    setAllowThread(false);
    setShowOnDispatch(true);
    setActive(true);
    setSelectedGroupIds([]);
    setFormTabs([]);
    setFormTabsPosition('after');
    if (searchParams.get('id')) {
      const next = new URLSearchParams(searchParams);
      next.delete('id');
      setSearchParams(next, { replace: true });
    }
  };

  const handleNew = async () => {
    if (!canManage) return;
    setCreating(true);
    setError(null);
    try {
      const created = await changesApi.createInternalSubtaskTemplate({
        name: 'Subtarefa interna',
      });
      await load();
      applyRowToForm(created);
      setMessage('Subtarefa interna criada. Monte os campos abaixo e vincule os grupos.');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível criar a subtarefa interna.'));
    } finally {
      setCreating(false);
    }
  };

  const saveMeta = async () => {
    if (editId == null) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Informe o nome da subtarefa interna.');
      return;
    }
    setSavingMeta(true);
    setError(null);
    try {
      await changesApi.updateInternalSubtaskTemplate(editId, {
        name: trimmed,
        default_title: defaultTitle.trim() || null,
        task_type_id: taskTypeId ? Number(taskTypeId) : null,
        allow_thread_default: allowThread,
        show_on_dispatch: showOnDispatch,
        is_active: active,
      });
      await changesApi.replaceInternalSubtaskTemplateGroups(editId, selectedGroupIds);
      await load();
      setMessage('Subtarefa interna salva.');
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a subtarefa interna.'));
    } finally {
      setSavingMeta(false);
    }
  };

  const handleToggleActive = async (row: InternalSubtaskTemplate) => {
    try {
      await changesApi.updateInternalSubtaskTemplate(row.id, { is_active: !row.is_active });
      await load();
    } catch (err: unknown) {
      showAlert({
        title: 'Erro',
        message: getApiErrorMessage(err, 'Não foi possível atualizar o status.'),
        variant: 'danger',
      });
    }
  };

  const handleDelete = async (row: InternalSubtaskTemplate) => {
    const linked = row.linked_group_count ?? 0;
    const ok = await showConfirm({
      title: 'Excluir subtarefa interna',
      message:
        linked > 0
          ? `«${row.name}» está vinculada a ${linked} grupo(s). Desvincular todos e excluir?`
          : `Excluir «${row.name}»?`,
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      if (linked > 0) {
        await changesApi.replaceInternalSubtaskTemplateGroups(row.id, []);
      }
      await changesApi.deleteInternalSubtaskTemplate(row.id);
      if (editId === row.id) closeEditor();
      await load();
    } catch (err: unknown) {
      showAlert({
        title: 'Não foi possível excluir',
        message: getApiErrorMessage(err, 'Falha ao excluir.'),
        variant: 'danger',
      });
    }
  };

  const toggleGroup = (groupId: number, checked: boolean) => {
    setSelectedGroupIds((prev) =>
      checked ? (prev.includes(groupId) ? prev : [...prev, groupId]) : prev.filter((id) => id !== groupId),
    );
  };

  const saveTabs = async (
    next: string[],
    extras?: { from?: string; to?: string; position?: 'before' | 'after' },
  ) => {
    if (editId == null) return;
    setSavingTabs(true);
    setError(null);
    try {
      const res = await changesApi.replaceInternalSubtaskFormTabs(editId, {
        items: next,
        rename_from: extras?.from,
        rename_to: extras?.to,
        position: extras?.position || formTabsPosition,
      });
      const savedTabs = res.items || next;
      const savedPosition = res.position === 'before' ? 'before' : extras?.position || formTabsPosition;
      setFormTabs(savedTabs);
      setFormTabsPosition(savedPosition);
      setItems((prev) =>
        prev.map((item) =>
          item.id === editId
            ? { ...item, form_tabs: savedTabs, form_tabs_position: savedPosition }
            : item,
        ),
      );
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar as abas.'));
    } finally {
      setSavingTabs(false);
    }
  };

  const fieldApi = useMemo(() => {
    if (editId == null) return null;
    const templateId = editId;
    return {
      load: (): Promise<{ items: ChangeFormField[] }> =>
        changesApi.listInternalSubtaskTemplateFields(templateId),
      createField: (payload: Record<string, unknown>) =>
        changesApi.createInternalSubtaskTemplateField(templateId, payload),
      updateField: (id: number, payload: Record<string, unknown>) =>
        changesApi.updateInternalSubtaskTemplateField(templateId, id, payload),
      deleteField: (id: number) => changesApi.deleteInternalSubtaskTemplateField(templateId, id),
      reorderFields: (ids: number[]) =>
        changesApi.reorderInternalSubtaskTemplateFields(templateId, ids),
    };
  }, [editId]);

  const groupNames = (row: InternalSubtaskTemplate) =>
    (row.linked_groups || []).map((item) => item.name).filter(Boolean).join(', ') || '—';

  if (canManage && hubTab === 'presets') {
    return (
      <div className="d-flex flex-column gap-3">
        <Nav variant="tabs" className="mb-1">
          <Nav.Item>
            <Nav.Link active={false} onClick={() => setHubTab('models')}>Modelos</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link active onClick={() => setHubTab('presets')}>Campos padrão e colunas</Nav.Link>
          </Nav.Item>
        </Nav>
        <ChangeInternalSubtaskPresetCatalogPanel />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {canManage ? (
        <Nav variant="tabs" className="mb-1">
          <Nav.Item>
            <Nav.Link active onClick={() => setHubTab('models')}>Modelos</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link active={false} onClick={() => setHubTab('presets')}>
              Campos padrão e colunas
            </Nav.Link>
          </Nav.Item>
        </Nav>
      ) : null}
      <Alert variant="light" className="border mb-0">
        Monte a subtarefa interna aqui e vincule aos grupos. Campos marcados como padrão do sistema
        vêm do cadastro Campos padrão e colunas (gestor). A opção de aparecer na modal de disparo
        fica nesta ficha, não no grupo.
      </Alert>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {message ? (
        <Alert variant="success" dismissible onClose={() => setMessage(null)}>
          {message}
        </Alert>
      ) : null}

      {editId == null ? (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <p className="text-muted small mb-0">
              {loading ? 'Carregando…' : `${items.length} subtarefa(s) interna(s)`}
            </p>
            {canManage ? (
              <Button size="sm" disabled={creating} onClick={() => void handleNew()}>
                {creating ? 'Criando…' : 'Nova subtarefa interna'}
              </Button>
            ) : null}
          </div>
          <Card>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Campos</th>
                    <th>Grupos</th>
                    <th>Status</th>
                    {canManage ? <th className="text-end">Ações</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {!loading && items.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 6 : 5} className="text-muted text-center py-4">
                        Nenhuma subtarefa interna ainda.
                      </td>
                    </tr>
                  ) : null}
                  {items.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.name}</strong>
                        {row.default_title ? (
                          <div className="small text-muted">Título na mudança: {row.default_title}</div>
                        ) : null}
                      </td>
                      <td className="small">{row.task_type_name || '—'}</td>
                      <td>{row.field_count ?? 0}</td>
                      <td className="small">{groupNames(row)}</td>
                      <td>
                        <Badge bg={row.is_active ? 'success' : 'secondary'}>
                          {row.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </td>
                      {canManage ? (
                        <td className="text-end">
                          <Button size="sm" variant="outline-primary" onClick={() => applyRowToForm(row)}>
                            Editar
                          </Button>{' '}
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => void handleToggleActive(row)}
                          >
                            {row.is_active ? 'Desativar' : 'Ativar'}
                          </Button>{' '}
                          <Button size="sm" variant="outline-danger" onClick={() => void handleDelete(row)}>
                            Excluir
                          </Button>
                        </td>
                      ) : (
                        <td className="text-end">
                          <Button size="sm" variant="outline-secondary" onClick={() => applyRowToForm(row)}>
                            Ver
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      ) : (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">{editing?.name || 'Subtarefa interna'}</h5>
            <Button size="sm" variant="outline-secondary" onClick={closeEditor}>
              Voltar à lista
            </Button>
          </div>
          <Card>
            <Card.Body>
              <h6 className="mb-3">Identificação</h6>
              <Form.Group className="mb-2">
                <Form.Label htmlFor="internal-subtask-name">Nome da subtarefa interna</Form.Label>
                <Form.Control
                  id="internal-subtask-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canManage}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Título que aparece na mudança (opcional)</Form.Label>
                <Form.Control
                  value={defaultTitle}
                  onChange={(e) => setDefaultTitle(e.target.value)}
                  disabled={!canManage}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Tipo de tarefa</Form.Label>
                <Form.Select
                  value={taskTypeId}
                  onChange={(e) => setTaskTypeId(e.target.value ? Number(e.target.value) : '')}
                  disabled={!canManage}
                >
                  <option value="">Selecione</option>
                  {taskTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Check
                type="switch"
                id="internal-show-on-dispatch"
                className="mb-2"
                label="Mostrar na modal de disparo"
                checked={showOnDispatch}
                disabled={!canManage}
                onChange={(e) => setShowOnDispatch(e.target.checked)}
              />
              <Form.Check
                type="switch"
                className="mb-2"
                label="Permitir comentários e anexos"
                checked={allowThread}
                disabled={!canManage}
                onChange={(e) => setAllowThread(e.target.checked)}
              />
              <Form.Check
                type="switch"
                className="mb-3"
                label="Subtarefa interna ativa"
                checked={active}
                disabled={!canManage}
                onChange={(e) => setActive(e.target.checked)}
              />
              <h6 className="mb-2">Grupos vinculados</h6>
              <p className="small text-muted">
                Marque os grupos que usam esta subtarefa. Um grupo pode ter várias internas; esta
                subtarefa também pode ficar em vários grupos.
              </p>
              {groups.length === 0 ? (
                <p className="small text-muted">Nenhum grupo ativo. Cadastre em Grupos.</p>
              ) : (
                <div className="d-flex flex-column gap-1 mb-3">
                  {groups.map((group) => (
                    <Form.Check
                      key={group.group_id}
                      type="checkbox"
                      id={`link-group-${group.group_id}`}
                      label={group.name || `Grupo #${group.group_id}`}
                      checked={selectedGroupIds.includes(group.group_id)}
                      disabled={!canManage}
                      onChange={(e) => toggleGroup(group.group_id, e.target.checked)}
                    />
                  ))}
                </div>
              )}
              {canManage ? (
                <Button size="sm" disabled={savingMeta} onClick={() => void saveMeta()}>
                  {savingMeta ? 'Salvando…' : 'Salvar identificação e vínculos'}
                </Button>
              ) : null}
            </Card.Body>
          </Card>
          <Card>
            <Card.Body>
              <ChangeInternalSubtaskTabsEditor
                tabs={formTabs}
                position={formTabsPosition}
                canManage={canManage}
                saving={savingTabs}
                onChange={saveTabs}
              />
            </Card.Body>
          </Card>
          {fieldApi ? (
            <ChangeFormFieldBuilder
              heading="Campos da subtarefa"
              description="Perguntas, tipo, obrigatório, condição e meia linha ou linha inteira. Escolha a aba ou deixe no formulário principal."
              api={fieldApi}
              formTabs={formTabs}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default ChangeInternalSubtaskTemplatesPanel;
